import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { testIds } from "@/constants/testIds";
import { toast } from "sonner";
import { Loader2, Check, Ban, Package, Clock } from "lucide-react";

export default function ItemsPendingView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/items/pending");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/items/${id}/status`, { status });
      toast.success(
        status === "approved"
          ? "Articolo approvato — visibile nel catalogo"
          : "Rimandato in attesa",
      );
      load();
    } catch {
      toast.error("Errore aggiornamento");
    }
  };

  const rejectDelete = async (id) => {
    if (!window.confirm("Rifiutare (eliminare) questo articolo?")) return;
    await api.delete(`/items/${id}`);
    toast.success("Articolo rifiutato ed eliminato");
    load();
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-amber-400" />
          <h2 className="font-heading text-lg sm:text-xl font-black uppercase tracking-tight text-white">
            Articoli in verifica
          </h2>
          {items.length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-mono uppercase tracking-widest">
              {items.length}
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-500 mt-1">
          Articoli caricati o modificati dai partner in attesa della tua verifica prima di apparire nel catalogo pubblico.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-zinc-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Caricamento...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-emerald-400 text-xs font-mono uppercase tracking-widest border border-dashed border-emerald-500/30 rounded-xl">
          ✓ Nessun articolo in attesa di verifica
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={testIds.adminPendingRow(it.id)}
              className="bg-zinc-900/90 border border-amber-900/40 rounded-xl p-3 sm:p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-zinc-950 border border-zinc-800 overflow-hidden shrink-0 flex items-center justify-center">
                  {it.image_path ? (
                    <img
                      src={fileUrl(it.image_path)}
                      alt={it.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package size={20} className="text-zinc-700" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-white text-sm break-words">
                    {it.name}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                      {it.category}
                    </span>
                    <span className="text-[10px] font-mono text-zinc-600">
                      · qty {it.quantity}
                    </span>
                    <span className="font-mono font-bold text-emerald-400 text-xs">
                      · €{it.daily_price.toFixed(2)}
                    </span>
                  </div>
                  {it.owner_name && (
                    <div className="text-[10px] font-mono text-rose-400 mt-0.5 uppercase tracking-widest truncate">
                      by {it.owner_name}
                    </div>
                  )}
                </div>
              </div>
              {it.description && (
                <div className="text-xs text-zinc-400 mt-2 pt-2 border-t border-zinc-800 break-words">
                  {it.description}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-zinc-800 flex flex-wrap gap-2">
                <button
                  data-testid={testIds.adminPendingApprove(it.id)}
                  onClick={() => setStatus(it.id, "approved")}
                  className="min-h-[36px] px-3 rounded-lg bg-emerald-600/15 hover:bg-emerald-600/25 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5"
                >
                  <Check size={12} /> Approva
                </button>
                <button
                  data-testid={testIds.adminPendingReject(it.id)}
                  onClick={() => rejectDelete(it.id)}
                  className="min-h-[36px] px-3 rounded-lg bg-zinc-800 hover:bg-rose-950 border border-zinc-700 text-zinc-400 hover:text-rose-400 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5"
                >
                  <Ban size={12} /> Rifiuta
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
